import { ReplaySubject, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { TreeChecklistNode } from "@common/data-models/complex-types/tree-checklist-node";
import { FieldDef } from "@common/data-models/service/field-def";
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { CustomFilterDef } from "@common/data-models/navigation/custom-filter-def";
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { AsyncServiceBase } from "@common/services/async-service-base";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { VisualStateService } from "@common/services/visual-state-service";
import { ExternalActionsService } from "@common/services/external-actions-service";
import { LanguageSettings } from "@common/services/language-settings";
import { LogonService } from "@common/services/logon-service";
import { Group } from "@common/data-models/service/group";
import { Server } from "@common/data-models/service/server";
import { Adorner } from "@common/data-models/service/adorner";
import { Staff } from "@common/data-models/service/staff";
import { ExternalAction } from "@common/data-models/service/external-action";
import { FieldMapping } from "@common/data-models/service/field-mapping";
import { ENPSLanguage } from "@common/data-models/complex-types/enps-language";
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { TimeZone } from "@common/data-models/complex-types/time-zone";
import { Record } from "@common/data-models/records/record";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { CriteriaObj } from "@common/data-models/criteria/criteria-obj";
import { GroupSecurityPrivs } from "@common/data-models/service/group-security-privs";
import { StorytellingNavigation } from "@common/data-models/service/storytelling-navigation"

import { IGlobalTablesService } from "@common/interfaces/i-global-tables-service";
import { Convertor } from "@common/services/convertor";
import { PlatformConvertor } from "@common/services/platform-convertor";

import { FilterKeywords } from "@common/components/filter/filter-keywords";
import { ResultGroupStaticServices } from "@common/data-models/search-results/result-group";

import { OPTION_ALL, OPTION_BLANK, OPTION_YES, OPTION_NO, PSTWEB_LIST_VALUE, ST_CRITERIA_SECURITY_ROLE } from "@common/enums/constants";
import { GroupPermissions, GroupCostPermissions } from '@common/data-models/service/group-permissions';
import { ListDataMetaData } from '@common/data-models/service/list-data';
import { DataType } from '@common/enums/data-type';
import { Path } from "@common/data-models/complex-types/path";
import { AppTheme } from "../../app.theme";
import { Util, GlobalRoles } from '../utils/util';
import * as XRegExp from 'xregexp';
import moment from 'moment-timezone';
import { Direction } from '@angular/cdk/bidi';
import { ObjectTypes } from '@common/enums/object-types';

type Criteria = {
    matches: RegExpMatchArray;
    criteria: string;
    buffer: { [id: string]: RegExpMatchArray };
}


@Injectable({ providedIn: 'root' })
export class GlobalTablesService extends AsyncServiceBase implements IGlobalTablesService {

    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public logonService: LogonService,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public externalActionsService: ExternalActionsService,
        public convertor: Convertor,
        public platformConvertor: PlatformConvertor
    ) {
        super();
        this.convertor.globalTablesService = this;
        this.platformConvertor.globalTablesService = this;
        FilterKeywords.globalTablesService = this;
        ResultGroupStaticServices.globalTablesService = this;
        Record.globalTablesService = this;
        AppTheme.globalTablesService = this;
        Path.globalTablesService = this;

        this.logonService.hasActiveSession.subscribe(() => {
            this.getGlobalTablesAndResolve();
        });
    }

    public globalTablesLoaded = new ReplaySubject<void>(1);

    public async getGlobalTablesAndResolve(): Promise<any> {
        await this.getGlobalTablesSimple();
        this.globalTablesLoaded.next();
        this.initializeResolve();
    }

    private calculateListItemTitle(propertyId: string, id: string, title: string): string {
        if (this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${propertyId}_${id}`).includes(PSTWEB_LIST_VALUE)) {
            return title;
        }
        return this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${propertyId}_${id}`);
    }

    private clearTitleCache() {
        this.listItemsCache = {};
        this.listItemsForTreeCache = {};
        this.listItemsForFieldCache = {};
        this.listItemsForDeskCache = [];
        this.listColumnsForSpecialFieldCache = {};
    }

    private calculateInitials(name: string): string {
        if (!name) {
            return "";
        }
        let initials = "";
        const words = name.split(" ");
        if (words.length > 1) {
            initials = `${words[0]?.charAt(0)?.toUpperCase()}${words[words.length - 1].charAt(0)?.toUpperCase()}`;
            return initials;
        } else {
            initials = `${words[0].charAt(0)?.toUpperCase()}${words[0].charAt(1)}`;
        }
        return initials;
    }

    private calculateEditableForDesk(id: string, type: ObjectTypes) {
        switch (type) {
            case ObjectTypes.TOPIC:
                return this.securityPrivileges[id].topic.write;
            case ObjectTypes.ASSIGNMENT:
                return this.securityPrivileges[id].assignment.write;
            case ObjectTypes.PSTVIEWSTATE:
                return true;
        }
        return false;
    }

    private fieldDefsLoadedSource = new Subject<boolean>();
    public FieldDefsLoadedObservable = this.fieldDefsLoadedSource.asObservable();

    public ImplementedValidationTypes: Array<string> = [
        'CHECKLIST',
        'COMBOBOX',
        'TEXT',
        'CHECKBOX',
        'TIME',
        'DATE',
        'MOSDATETIME',
        'STAFFIDLIST',
        'COST',
        'SPECIAL',
        'TREE',
        'LOOKUPLIST',
        'ADDRESS',
        'DATETIMERANGE'
    ];

    public PropertiesGroupKey: string = "PROPERTIES";

    public Security: Array<any> = [];
    public Apps: Array<any> = [];
    public Groups: { [id: string]: Group } = {};
    public GroupsArray: Array<Group> = [];
    public EnterpriseGroup: Group;
    public PlaybookTeamsArray: Array<Group> = [];
    public DefaultPlaybookTeam: Group = null;
    public EnterprisePlaybookGroup: string = null;

    public FieldMappings: Array<FieldMapping> = [];

    public AppVersion: String = "";
    public PlaybookHelpUrl: string;
    public setPlaybookHelpUrl() {
        if (this.visualStateService.ConfigSettings["PlaybookHelpUrl"]) {
            this.PlaybookHelpUrl = this.visualStateService.ConfigSettings["PlaybookHelpUrl"];
            if (this.PlaybookHelpUrl.indexOf("$version$") > -1) {
                this.PlaybookHelpUrl = this.PlaybookHelpUrl.replace("$version$", this.AppVersion.replace(/\./g, ""));
            }
        }
    }

    public MOSDevices: Array<any> = [];
    public Privileges: Array<any> = [];
    public Servers: { [id: string]: Server } = {};
    public Adorners: { [id: string]: Adorner } = {};

    public adornersIDS = {
        TopicCardLeftBorder: "TopicCardLeftBorder",
        AssignmentIcon: "AssignmentIcon",
        AssignmentCardLeftBorder: "AssignmentCardLeftBorder",
        TopicCardTypeColor: "TopicCardTypeColor",
        TopicIcon: "TopicIcon"
    }

    public WireCategories: Array<TreeChecklistNode> = [];
    public WireProviders: Array<{ id: string, title: string }> = [];

    public FieldDefs: Array<FieldDef> = [];
    public FieldDefsDict: { [id: string]: FieldDef } = {};

    public StorytellingNavigation: Array<StorytellingNavigation> = [];
    public StorytellingNavigationDict: { [product: string]: StorytellingNavigation } = {};

    public CustomFilterDef: Array<CustomFilterDef> = [];
    public CustomFilterDefsDict: { [id: string]: CustomFilterDef } = {};

    public Languages: Array<ENPSLanguage> = [];

    public PersonalLanguage: ENPSLanguage = null;

    public CommonTimeZones: Array<TimeZone> = [];
    public IanaTimeZones: Array<TimeZone> = [];
    public PersonalTimeZone: TimeZone = null;
    public PersonalTimeZoneDisplay: TimeZone = null;
    public ClientTimeZone: TimeZone = null;
    public DefaultTimeZone: TimeZone = null;

    public FilterDefs: { [id: number]: Array<FilterDef> } = {};
    public ViewStateOptionsDefs: { [type: number]: { [field: string]: ViewStateOptions } } = {};

    public getGroupingOptions: { [id: number]: () => ViewStateOptions } = {};
    public getSortingOptions: { [id: number]: () => ViewStateOptions } = {};
    public getLayoutOptions: { [id: number]: () => ViewStateOptions } = {};
    public calculateDefaultGroupSort: { [id: number]: () => void } = {};
    public clearServices: { [id: number]: () => void } = {};
    public getOptions(type: number, field: string): ViewStateOptions {
        if (this.ViewStateOptionsDefs[type] && this.ViewStateOptionsDefs[type][field]) {
            return this.ViewStateOptionsDefs[type][field];
        }
        let options: ViewStateOptions = new ViewStateOptions();

        if ((field == PSTFieldNames.PSTViewStateGrouping) && this.getGroupingOptions[type]) {
            options = this.getGroupingOptions[type]();
        }
        if ((field == PSTFieldNames.PSTViewStateSorting) && this.getSortingOptions[type]) {
            options = this.getSortingOptions[type]();
        }
        if ((field == PSTFieldNames.PSTViewStateLayout) && this.getLayoutOptions[type]) {
            options = this.getLayoutOptions[type]();
        }

        if (!this.ViewStateOptionsDefs[type]) this.ViewStateOptionsDefs[type] = {};
        this.ViewStateOptionsDefs[type][field] = options;
        return this.ViewStateOptionsDefs[type][field];
    }

    public get DefaultCheckListValue(): MultiValue<string> {
        return new MultiValue<string>();
    }

    public propertiesGroupCache: { [id: string]: string } = {};
    public staticPropertyHandlers: { [id: string]: () => { [id: string]: boolean } } = {};
    private dynamicPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getDynamicPropertiesFor(type: string, group: string): Array<FieldDef> {
        let cacheKey = type + "_" + group;
        if (this.dynamicPropertiesCache[cacheKey]) return this.dynamicPropertiesCache[cacheKey];
        let excluded: { [id: string]: boolean } = {};
        if (this.staticPropertyHandlers[type.toUpperCase()]) {
            excluded = this.staticPropertyHandlers[type.toUpperCase()]();
        }
        if (this.FieldDefs.length > 0) {
            let ret = this.FieldDefs.filter((item) => {
                if (excluded && excluded[item.ID]) return false;
                if (item.Validation.toUpperCase() == 'SPECIAL') return false;
                if (item.Validation.toUpperCase() == 'COST') {
                    this.propertiesGroupCache[item.ID] = "Cost";
                    return false;
                }
                if (type == "PublicationChannel") {
                    return (item["PublicationChannelProp"] == true);
                }
                if (type == "Script") {
                    return ((item.FieldGroup.trim() == group) && (
                        (item["Type"].indexOf("Script") >= 0) ||
                        (item["Type"].indexOf("RO Column") >= 0) ||
                        (item["Type"].indexOf("RO+NG Column") >= 0)
                    ));
                }
                return ((item[type + "Prop"] == true) && (
                    (item.FieldGroup.trim() == group) ||
                    ((group == "") && (item.FieldGroup.trim().toUpperCase() == this.PropertiesGroupKey))
                ));
            });
            ret.sort((a: FieldDef, b: FieldDef) => {
                return a.Sequence - b.Sequence;
            });
            this.dynamicPropertiesCache[cacheKey] = ret;

            for (let prop of ret) {
                this.propertiesGroupCache[prop.ID] = group;
            }

            return this.dynamicPropertiesCache[cacheKey];
        } else {
            return [];
        }
    }

    public isGroupSingleValue: boolean = false;
    public setIsGroupSingleValue() {
        if (this.visualStateService.ConfigSettings["EnablePlaybookSingleGroupSelection"] == "1") {
            this.isGroupSingleValue = true;
        } else {
            this.isGroupSingleValue = false;
        }
    }

    public EnforceRequiredFieldsOnCreate: boolean = false;
    public setEnforceRequiredFieldsOnCreate() {
        if (this.visualStateService.ConfigSettings["EnforceRequiredFieldsOnCreate"]) {
            this.EnforceRequiredFieldsOnCreate = !!parseInt(this.visualStateService.ConfigSettings["EnforceRequiredFieldsOnCreate"]);
        }
    }

    private allPropertyGroupsCache: { [id: string]: Array<{ name: string, dynamic: boolean }> } = {};
    public getAllPropertyGroupsFor(type: string): Array<{ name: string, dynamic: boolean }> {
        if (this.allPropertyGroupsCache[type]) return this.allPropertyGroupsCache[type];
        let allPropertyGroups: Array<{ name: string, dynamic: boolean }> = [];
        //field def groups
        let fieldGroups: Array<string> = this.getFieldGroupsFor(type);
        for (let group of fieldGroups) {
            allPropertyGroups.push({ name: group, dynamic: true });
        }
        //add Assignments and Attachments
        allPropertyGroups.push({ name: this.languageSettings.PSTWebCoverageAssignmentsPane, dynamic: false });
        allPropertyGroups.push({ name: this.languageSettings.PSTWebAttachmentsPane, dynamic: false });

        if (Util.directoryApp(this.StorytellingNavigation)) {
            allPropertyGroups.push({ name: this.languageSettings.PSTWebContactsPane, dynamic: false });
        }

        //specials
        let specials: Array<FieldDef> = [];
        if (type.toUpperCase() == "TOPIC") {
            specials = this.getSpecialPropertiesFor("Topic");
        }
        if (type.toUpperCase() == "ASSIGNMENT") {
            specials = this.getSpecialPropertiesFor("Assignment");
        }
        for (let spec of specials) {
            allPropertyGroups.push({ name: spec.DefaultCaption, dynamic: false });
        }

        if (this.visualStateService.ConfigSettings["FieldGroupsOrder"]) {
            let allSortedFieldGroups: Array<string> = this.visualStateService.ConfigSettings["FieldGroupsOrder"].split("|");
            let currentType = allSortedFieldGroups.find(field => field.toUpperCase().startsWith(type.toUpperCase()));
            if (currentType) {
                let fieldGroups = currentType.split("-")[1];
                if (fieldGroups) {
                    let sortedFieldGroups = fieldGroups.split(",");
                    if (sortedFieldGroups.indexOf("Properties") > -1) sortedFieldGroups.splice(sortedFieldGroups.indexOf("Properties"), 1, "");
                    allPropertyGroups.sort(function (a, b) {
                        let indexA = (sortedFieldGroups.indexOf(a.name) >= 0) ? sortedFieldGroups.indexOf(a.name) : 1000;
                        let indexB = (sortedFieldGroups.indexOf(b.name) >= 0) ? sortedFieldGroups.indexOf(b.name) : 1000;
                        return indexA - indexB;
                    });
                }
            }
        }
        this.allPropertyGroupsCache[type] = allPropertyGroups;
        return this.allPropertyGroupsCache[type];
    }

    private fieldGroupsCache: { [id: string]: Array<string> } = {};
    public getFieldGroupsFor(type: string): Array<string> {
        if (this.fieldGroupsCache[type]) return this.fieldGroupsCache[type];
        if (this.FieldDefs.length > 0) {
            let map: { [id: string]: boolean } = {};
            for (let field of this.FieldDefs) {
                if (type == "Script") {
                    if (
                        (field["Type"].indexOf("Script") >= 0) ||
                        (field["Type"].indexOf("RO Column") >= 0) ||
                        (field["Type"].indexOf("RO+NG Column") >= 0)
                    ) {
                        map[field.FieldGroup] = true;
                    }
                } else {
                    if (field[type + "Prop"] == true && !(field.Validation == 'Cost' || field.Validation == 'Special')) map[field.FieldGroup] = true;
                }
            }
            let ret: Array<string> = [""];
            for (let key in map) {
                if ((key.trim() !== "") && (key.trim().toUpperCase() != this.PropertiesGroupKey)) {
                    ret.push(key.trim());
                }
            }
            this.fieldGroupsCache[type] = ret;
        } else {
            return [];
        }

        return this.fieldGroupsCache[type];
    }

    public getExternalFilesFieldDef(): FieldDef {
        return this.FieldDefs.filter((item) => {
            return (item.ID == "ExternalFiles");
        })[0];
    }

    private specialPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getSpecialPropertiesFor(type: string): Array<FieldDef> {
        if (this.specialPropertiesCache[type]) return this.specialPropertiesCache[type];
        let excluded: { [id: string]: boolean } = null;
        if (this.FieldDefs.length > 0) {
            let ret = this.FieldDefs.filter((item) => {
                return (((item.Validation.toUpperCase() == "SPECIAL") || (item.Validation.toUpperCase() == "COST")) && (item[type + "Prop"] == true));
            });
            ret.sort((a: FieldDef, b: FieldDef) => {
                return a.Sequence - b.Sequence;
            });
            this.specialPropertiesCache[type] = ret;
            return this.specialPropertiesCache[type];
        } else {
            return [];
        }
    }

    private costPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getCostPropertiesFor(type: string): Array<FieldDef> {
        if (this.costPropertiesCache[type]) return this.costPropertiesCache[type];
        if (this.FieldDefs.length > 0) {
            let ret = this.FieldDefs.filter((item) => {
                return ((item.Validation.toUpperCase() == "COST") && (item[type + "Prop"] == true));
            });
            this.costPropertiesCache[type] = ret;
            return this.costPropertiesCache[type];
        } else {
            return [];
        }
    }

    private addressPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getAddressPropertiesFor(type: string): Array<FieldDef> {
        if (this.addressPropertiesCache[type]) return this.addressPropertiesCache[type];
        let excluded: { [id: string]: boolean } = null;
        if (this.FieldDefs.length > 0) {
            let ret = this.FieldDefs.filter((item) => {
                return ((item.Validation.toUpperCase() == "ADDRESS") && (item[type + "Prop"] == true));
            });
            ret.sort((a: FieldDef, b: FieldDef) => {
                return a.Sequence - b.Sequence;
            });
            this.addressPropertiesCache[type] = ret;
            return this.addressPropertiesCache[type];
        } else {
            return [];
        }
    }

    public isDateProperty(id: string): boolean {
        const prop = this.FieldDefsDict[id] || ListDataMetaData[id];
        if (prop && ((prop.Validation && ['TIME', 'DATE', 'MOSDATETIME'].indexOf(prop.Validation.toUpperCase()) > -1) || (prop == DataType.DATE))) {
            return true;
        }
        return false;
    }

    private rolePropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getRolePropertiesFor(type: string): Array<FieldDef> {
        if (this.rolePropertiesCache[type]) return this.rolePropertiesCache[type];
        if (this.FieldDefs.length > 0) {
            let ret = this.FieldDefs.filter((item) => {
                return ((item.Validation.toUpperCase() == "STAFFIDLIST") && (item[type + "Prop"] == true));
            });
            ret.sort((a: FieldDef, b: FieldDef) => {
                return a.Sequence - b.Sequence;
            });
            this.rolePropertiesCache[type] = ret;
            return this.rolePropertiesCache[type];
        } else {
            return [];
        }
    }

    public getFiltersForViewStateType: { [id: number]: () => Array<FilterDef> } = {};

    private supportedFilterValidations: Array<string> = [
        'TEXT', 'PSTPUBLICATIONLISTTIME', 'TIME', 'DATE', 'MOSDATETIME', 'DATETIMERANGE', 'CHECKLIST', 'CHECKLIST', 'COMBOBOX', 'CHECKBOX', 'STAFFIDLIST', 'LOOKUPLIST', 'TREE'
    ];

    public getFilters(type: number): Array<FilterDef> {
        if (this.FilterDefs[type]) {
            return this.FilterDefs[type];
        }
        let filterDefinitions: Array<FilterDef> = [];
        let fieldDefinitions: Array<FieldDef> = [];
        if (this.getFiltersForViewStateType[type]) {
            fieldDefinitions = this.FieldDefs.filter((item) => {
                return this.supportedFilterValidations.indexOf(item.Validation.toUpperCase()) > -1;
            }).filter(this.getFiltersForViewStateType[type]);
        }
        filterDefinitions = fieldDefinitions.sort((a: FieldDef, b: FieldDef) => {
            if (a.Sequence > b.Sequence) return 1;
            else if (a.Sequence < b.Sequence) return -1;
            else return 0;
        }).map((item) => {
            let filter: FilterDef = new FilterDef(item, null);

            if (filter.Validation == 'CHECKLIST' || filter.Validation == 'COMBOBOX') {
                if (filter.ID == PSTFieldNames.PSTTopicDepartment) {
                    filter.Options = [
                        { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                        { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true }
                    ].concat(this.getListItemsForDesk(ObjectTypes.PSTVIEWSTATE));
                } else if (filter.ID == PSTFieldNames.PSTWireProvider) {
                    filter.Options = [
                        { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                        { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true }
                    ].concat(this.getListItemsForWireProvider());
                } else if (filter.ID == PSTFieldNames.PSTWireCategory) {
                    let allOption: TreeChecklistNode = new TreeChecklistNode();
                    allOption.children = [];
                    allOption.id = OPTION_ALL;
                    allOption.title = this.languageSettings.PSTWebAllOption;
                    filter.Options = [
                        allOption
                    ].concat(this.getListItemsForWireCategory());
                } else if (filter.ID == PSTFieldNames.PSTTeamLocation) {
                    let teamsArr = this.getListItemsForTeamLocations();
                    if (teamsArr.length > 0) {
                        filter.Options = [
                            { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                            { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true },
                        ].concat(teamsArr);
                    } else {
                        return null;
                    }
                } else {
                    filter.Options = [
                        { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                        { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true }
                    ].concat(this.getListItems(filter.ID, item.List));
                }
            }
            if (filter.Validation == 'CHECKBOX') {
                filter.Options = [
                    { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption },
                    { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption },
                    { id: OPTION_YES, title: this.languageSettings.PSTWebYesOption },
                    { id: OPTION_NO, title: this.languageSettings.PSTWebNoOption }
                ];
            }
            if (filter.Validation == 'TREE') {
                filter.Options = [
                    { id: OPTION_ALL, value: this.languageSettings.PSTWebAllOption, children: [] },
                    { id: OPTION_BLANK, value: this.languageSettings.PSTWebBlankOption, children: [] }
                ].concat(this.getlistItemsForTree(item.List).map((item) => {
                    return { id: item.id, value: item.value, children: item.children };
                }));
            }
            return filter;
        }).filter(f => f);
        this.FilterDefs[type] = filterDefinitions;
        return this.FilterDefs[type];
    }

    private listItemsCache: { [id: string]: Array<{ id: string, title: string, selectable: boolean }> } = {};
    public getListItems(fieldID: string, listString: string): Array<{ id: string, title: string, selectable: boolean }> {
        if (this.listItemsCache[listString]) return this.listItemsCache[listString];
        this.listItemsCache[listString] = [];
        let chunks = listString ? listString.split("|") : [];
        for (let c of chunks) {
            this.listItemsCache[listString].push({ id: c, title: this.calculateListItemTitle(fieldID, c, c), selectable: true });
        }
        this.listItemsCache[listString];
        return this.listItemsCache[listString];
    }

    private listItemsForTreeCache: { [id: string]: Array<any> } = {};
    public getlistItemsForTree(listString: string): Array<any> {
        if (this.listItemsForTreeCache[listString]) return this.listItemsForTreeCache[listString];
        this.listItemsForTreeCache[listString] = [];
        let chunks = listString ? listString.split("|") : [];
        for (let c in chunks) {
            for (let field of this.FieldDefs) {
                if (field.Validation == 'Tree') {
                    let nodeChunks = chunks[c].split("\\");
                    // const nodeChunkTranslation = this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${field.ID}_${nodeChunks[0]}`).includes(PSTWEB_LIST_VALUE) ? nodeChunks[0] : this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${field.ID}_${nodeChunks[0]}`);
                    const nodeChunkTranslation = this.calculateListItemTitle(field.ID, nodeChunks[0], nodeChunks[0]);
                    let root = {
                        value: nodeChunkTranslation,
                        id: nodeChunks[0],
                        children: []
                    };
                    this.populateChildren(root, nodeChunks.splice(1, nodeChunks.length - 1), field.ID);
                    this.listItemsForTreeCache[listString].push(root);
                }
            }
        }
        this.listItemsForTreeCache[listString];
        return this.listItemsForTreeCache[listString];
    }

    public populateChildren(root, nodeChunks, fieldId) {
        let currentNode = root;
        while (nodeChunks.length > 0) {
            let chunk = nodeChunks.shift();
            // const chunkTranslation = this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${fieldId}_${chunk}`).includes(PSTWEB_LIST_VALUE) ? chunk : this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${fieldId}_${chunk}`);
            const chunkTranslation = this.calculateListItemTitle(fieldId, chunk, chunk);
            if (chunk == "+") {
                this.populateChildren(currentNode, nodeChunks, fieldId);
            } else if (chunk == "-") {
                return;
            } else {
                if (!root.children) root.children = [];
                root.children.push({ value: chunkTranslation, id: chunk, parentId: root.id });
                currentNode = root.children[root.children.length - 1];
            }
        }
    }

    private listItemsForFieldCache: { [id: string]: Array<{ id: string, title: string }> } = {};
    public getListItemsForField(field: string): Array<{ id: string, title: string }> {
        if (this.listItemsForFieldCache[field]) return this.listItemsForFieldCache[field];

        if (this.FieldDefs.length > 0) {
            for (let f of this.FieldDefs) {
                if (f.ID == field) {
                    let chunks = f.List ? f.List.split("|") : [];
                    this.listItemsForFieldCache[field] = [];
                    for (let c of chunks) {
                        this.listItemsForFieldCache[field].push({ id: c, title: this.calculateListItemTitle(field, c, c) });
                    }
                    break;
                }
            }
            this.listItemsForFieldCache[field];
            return this.listItemsForFieldCache[field];
        } else {
            return [];
        }
    }

    private defaultCaptionForFieldCache: { [id: string]: string } = {};
    public defaultCaptionForField(fieldId: string): string {
        if (this.defaultCaptionForFieldCache[fieldId]) return this.defaultCaptionForFieldCache[fieldId];

        if (this.FieldDefs.length > 0) {
            for (let f of this.FieldDefs) {
                if (f.ID == fieldId) {
                    this.defaultCaptionForFieldCache[fieldId] = f.DefaultCaption;
                    break;
                }
            }
            return this.defaultCaptionForFieldCache[fieldId];
        } else {
            return "";
        }
    }

    private fieldDefCache: { [id: string]: FieldDef } = {};
    public getFieldDef(fieldId: string): FieldDef {
        if (this.fieldDefCache[fieldId]) return this.fieldDefCache[fieldId];
        for (let fieldDef of this.FieldDefs) {
            if (fieldDef.ID.toUpperCase() == fieldId.toUpperCase()) {
                this.fieldDefCache[fieldId] = fieldDef;
                break;
            }
        }
        return this.fieldDefCache[fieldId];
    }

    private isSpecialFieldTypeCache: { [id: string]: boolean } = {};
    public isSpecialFieldType(fieldId: string): boolean {
        if (!(this.isSpecialFieldTypeCache[fieldId] == undefined)) return this.isSpecialFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && ((f.Validation.toUpperCase() == 'SPECIAL') || (f.Validation.toUpperCase() == 'COST'))) {
                this.isSpecialFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isSpecialFieldTypeCache[fieldId];
    }

    private isCostFieldTypeCache: { [id: string]: boolean } = {};
    public isCostFieldType(fieldId: string): boolean {
        if (!(this.isCostFieldTypeCache[fieldId] == undefined)) return this.isCostFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && (f.Validation.toUpperCase() == 'COST')) {
                this.isCostFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isCostFieldTypeCache[fieldId];
    }

    private customItemsFieldType = {
        "PSTViewStateTopicCardCustomItems": true
    }
    public isCustomItemFieldType(fieldId: string): boolean {
        return this.customItemsFieldType[fieldId];
    }

    private isLookupListFieldTypeCache: { [id: string]: boolean } = {};
    public isLookupListFieldType(fieldId: string): boolean {
        if (!(this.isLookupListFieldTypeCache[fieldId] == undefined)) return this.isLookupListFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'LOOKUPLIST') {
                this.isLookupListFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isLookupListFieldTypeCache[fieldId];
    }

    private isDateRangeFieldTypeCache: { [id: string]: boolean } = {};
    public isDateRangeFieldType(fieldId: string): boolean {
        if (!(this.isDateRangeFieldTypeCache[fieldId] == undefined)) return this.isDateRangeFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'DATETIMERANGE') {
                this.isDateRangeFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isDateRangeFieldTypeCache[fieldId];
    }

    private isComboboxFieldTypeCache: { [id: string]: boolean } = {};
    public isComboboxFieldType(fieldId: string): boolean {
        if (!(this.isComboboxFieldTypeCache[fieldId] == undefined)) return this.isComboboxFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'COMBOBOX') {
                this.isComboboxFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isComboboxFieldTypeCache[fieldId];
    }

    private isLocationListFieldTypeCache: { [id: string]: boolean } = {};
    public isLocationListFieldType(fieldId: string): boolean {
        if (!(this.isLocationListFieldTypeCache[fieldId] == undefined)) return this.isLocationListFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'LOCATIONLIST') {
                this.isLocationListFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isLocationListFieldTypeCache[fieldId];
    }

    private isStaffIDListFieldTypeCache: { [id: string]: boolean } = {
        "ParentOwner": true
    };
    public isStaffIDListFieldType(fieldId: string): boolean {
        if (!(this.isStaffIDListFieldTypeCache[fieldId] == undefined)) return this.isStaffIDListFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'STAFFIDLIST') {
                this.isStaffIDListFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isStaffIDListFieldTypeCache[fieldId];
    }

    private isAddressFieldTypeCache: { [id: string]: boolean } = {
        "ParentLocation": true
    };
    public isAddressFieldType(fieldId: string): boolean {
        if (!(this.isAddressFieldTypeCache[fieldId] == undefined)) return this.isAddressFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'ADDRESS') {
                this.isAddressFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isAddressFieldTypeCache[fieldId];
    }

    private isCheckListFieldTypeCache: { [id: string]: boolean } = {
        "ParentCategory": true,
        "ParentType": true
    };
    public isCheckListFieldType(fieldId: string): boolean {
        if (!(this.isCheckListFieldTypeCache[fieldId] == undefined)) return this.isCheckListFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'CHECKLIST') {
                this.isCheckListFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isCheckListFieldTypeCache[fieldId];
    }

    private isComboBoxFieldTypeCache: { [id: string]: boolean } = {};
    public isComboBoxFieldType(fieldId: string): boolean {
        if (!(this.isComboBoxFieldTypeCache[fieldId] == undefined)) return this.isComboBoxFieldTypeCache[fieldId];
        for (let f of this.FieldDefs) {
            if (f.ID == fieldId && f.Validation.toUpperCase() == 'COMBOBOX') {
                this.isComboBoxFieldTypeCache[fieldId] = true;
                break;
            }
        }
        return this.isComboBoxFieldTypeCache[fieldId];
    }

    private isDateTimeFieldTypeCache: { [id: string]: boolean } = {};
    public isDateTimeFieldType(fieldId: string): boolean {
        return this.isDateTimeFieldTypeCache[fieldId];
    }

    private propertyWithOperatorAndValuePattern = XRegExp('([\\p{L}\\p{Nd}_]+?)([ =><!\\s]+)(\'?[\\p{L}\\p{Nd}_\\-\\s]*\'?)( \\(([\\p{Nd}_\\s]*)([\\p{L}\\p{Nd}]*)\\)\\s*)?', 'gu');
    private identifiersPattern = XRegExp('[\\p{L}\\p{Nd}_\\s]+?[=><! ]+\'?[\\p{L}\\p{Nd}_\\-\\s]*\'?(\\([\\p{Nd}]+\\s?([\\p{L}])*\\))?', 'gu');
    private timeBufferPattern = XRegExp('([\\p{L}\\p{Nd}_\\s]+?)\\(([\\p{Nd}_\\s]*)([\\p{L}]*)\\)\\s*', 'u');
    private getCriteriaMatches(criteria: string): Criteria {
        const operatorANDPattern = /(\b\s?AND\s?\b)/g;
        const operatorORPattern = /(\b\s?OR\s?\b)/g;


        criteria = criteria.replace(operatorANDPattern, " && ");
        criteria = criteria.replace(operatorORPattern, " || ");
        const matches = criteria.match(this.identifiersPattern);
        const bufferMap = {};
        for (const match of matches) {
            bufferMap[match] = match.match(this.timeBufferPattern);
        }
        return { matches: matches, criteria: criteria, buffer: bufferMap };
    }

    private parseCriteriaChunks(chunks: string[], criteriaObj: CriteriaObj, buffer: RegExpMatchArray): string {
        if (chunks.length > 1) {
            const left = chunks[1].trim();
            const right = chunks[3].trim();
            let operator = chunks[2].trim();

            let compareFields = false;
            for (let objType in Record.allPropertiesCache) {
                if (Record.allPropertiesCache[objType][left] || ListDataMetaData[left] || left == ST_CRITERIA_SECURITY_ROLE) {
                    criteriaObj.hasPropForObjectType[objType] = true;
                }
                if (right && (Record.allPropertiesCache[objType][right] || ListDataMetaData[right])) {
                    compareFields = true;
                }
            }
            let isNull: boolean = right.toString().toLowerCase() == "null";
            const isSecurityRole = left == ST_CRITERIA_SECURITY_ROLE;
            const securityRole = this.securityPrivileges[this.nomWebServicesJson.HelloResults.HomeProgram]?.privId || null;
            let prop = "";
            if (operator == '=') operator = '==';
            if (this.isCheckListFieldType(left)) {
                // Left side is checklist field
                if (operator == '==') {
                    if (isNull) {
                        prop += "!rec." + left + " || !rec." + left + ".length";
                    } else {
                        prop += "rec." + left + " && rec." + left + ".includes(" + right + ")";
                    }
                }
                if (operator == '!=') {
                    if (isNull) {
                        prop += "rec." + left + " && rec." + left + ".length";
                    } else {
                        prop += "!rec." + left + " || !rec." + left + ".includes(" + right + ")";
                    }
                }
            } else {
                let possibleNumber = parseFloat(right)
                if (!isNaN(possibleNumber)) {
                    // Right side is a number
                    if (operator == '==') {
                        prop += "rec." + left + operator + right;
                    }
                    if (operator == '!=') {
                        prop += "!rec." + left + " || rec." + left + operator + right;
                    }
                } else {
                    if (compareFields) {
                        // Right side is a field
                        if (this.isDateProperty(left)) {
                            prop += "((!!rec." + left + ") ? rec." + left + ".get() : false)";
                        } else {
                            //
                            if(isSecurityRole) {
                                prop += `'${securityRole}'`;
                            } else {
                                prop += "rec." + left;
                            }
                        }
                        if (this.isDateProperty(right)) {
                            if (buffer) {
                                if (operator == '>') {
                                    prop += ' - ' + "((!!rec." + right + ") ? rec." + right + ".get() : false)" + ' >= ' + parseInt(buffer[2]) * Util.timeBufferGranularity(buffer[3]);
                                }
                                if (operator == '<') {
                                    prop += ' - ' + "((!!rec." + right + ") ? rec." + right + ".get() : false)" + ' <= ' + parseInt(buffer[2]) * (-1 * Util.timeBufferGranularity(buffer[3]));
                                }
                            } else {
                                prop += operator + "((!!rec." + right + ") ? rec." + right + ".get() : false)";
                            }
                        } else {
                            prop += operator + "rec." + right;
                        }
                    } else {
                        try {
                            if (isNull) {
                                // Right side is null
                                if (this.isDateProperty(left)) {
                                    if (operator == "==") {
                                        prop += "!rec." + left + " || rec." + left + ".isBlank()";
                                    }

                                    if (operator == "!=") {
                                        prop += "!!rec." + left + " && !rec." + left + ".isBlank()";
                                    }
                                } else {
                                    if (operator == "==") {

                                        if(isSecurityRole) {
                                            prop += `${!securityRole}`;
                                        } else {
                                            prop += "!rec." + left;
                                        }
                                    }
                                    if (operator == "!=") {
                                        if(isSecurityRole) {
                                            prop += `${!!securityRole}`;
                                        } else {
                                            prop += "!!rec." + left;
                                        }
                                    }
                                }
                            } else {
                                if (this.isDateProperty(left)) {
                                    prop += "(!!rec." + left + ") ? rec." + left + ".get()";
                                } else {
                                    if (isSecurityRole) {
                                        prop += `('${securityRole}')?('${securityRole}'`;
                                    } else {
                                        prop += "(rec." + left + ")? (rec." + left;
                                    }
                                }
                                prop += operator + right + ") : false";
                            }
                        } catch (error) {
                            console.log(left);
                            console.log(error);
                        }
                    }
                }
            }
            return prop;
        }
        return null;
    }

    public isPropertyForType: { [id: number]: (fieldDef: FieldDef) => boolean } = {};
    public constructCriteriaMappings() {
        Record.allPropertiesCache = {};
        for (let objectType in this.isPropertyForType) {
            Record.allPropertiesCache[objectType] = {};
        }
        for (let field in this.FieldDefsDict) {
            for (let objectType in this.isPropertyForType) {
                if (this.isPropertyForType[objectType](this.FieldDefsDict[field])) {
                    Record.allPropertiesCache[objectType][field] = this.FieldDefsDict[field];
                }
            }
        }

        let combinedArray = [...this.FieldDefs, ...this.externalActionsService.ExternalActions];
        for (let f of combinedArray) {
            if (f.VisibilityCriteria && f.VisibilityCriteria.trim().length > 0) {
                let m = this.getCriteriaMatches(f.VisibilityCriteria);
                f.VisibilityCriteria = m.criteria;
                let visibilityCriteriaObj = new CriteriaObj();
                if (m.matches) {
                    for (let match of m.matches) {

                        let chunks = match.trim().split(this.propertyWithOperatorAndValuePattern);
                        if (chunks.length > 1) {
                            let propName = chunks[1].trim();
                            let prop = this.parseCriteriaChunks(chunks, visibilityCriteriaObj, m.buffer[match]);
                            if (prop !== null) {
                                f.VisibilityCriteria = f.VisibilityCriteria.replace(match.trim(), prop);
                            }
                            if (propName == ST_CRITERIA_SECURITY_ROLE) {
                                if (!Record.visibilityCriteriaReverseCache[PSTFieldNames.PSTCreatedBy]) Record.visibilityCriteriaReverseCache[PSTFieldNames.PSTCreatedBy] = [];
                                Record.visibilityCriteriaReverseCache[PSTFieldNames.PSTCreatedBy].push(f.ID);
                            } else {
                                if (!Record.visibilityCriteriaReverseCache[propName]) Record.visibilityCriteriaReverseCache[propName] = [];
                                Record.visibilityCriteriaReverseCache[propName].push(f.ID);
                            }


                            if (chunks[3] && ListDataMetaData[chunks[3].trim()]) {
                                if (!Record.visibilityCriteriaReverseCache[chunks[3].trim()]) Record.visibilityCriteriaReverseCache[chunks[3].trim()] = [];
                                Record.visibilityCriteriaReverseCache[chunks[3].trim()].push(f.ID);
                            }
                        }
                    }
                    visibilityCriteriaObj.criteria = f.VisibilityCriteria;
                    Record.visibilityCriteriaCache[f.ID] = visibilityCriteriaObj;
                }
            }

            //Read Only Criteria
            if (f instanceof FieldDef && f.ReadOnlyCriteria && f.ReadOnlyCriteria.trim().length > 0) {
                let m = this.getCriteriaMatches(f.ReadOnlyCriteria);
                f.ReadOnlyCriteria = m.criteria;
                let readOnlyCriteriaObj = new CriteriaObj();
                if (m.matches) {
                    for (let match of m.matches) {
                        let chunks = match.trim().split(this.propertyWithOperatorAndValuePattern);
                        if (chunks.length > 1) {
                            let propName = chunks[1].trim();
                            let prop = this.parseCriteriaChunks(chunks, readOnlyCriteriaObj, m.buffer[match]);
                            if (prop !== null) {
                                f.ReadOnlyCriteria = f.ReadOnlyCriteria.replace(match.trim(), prop);
                            }

                            if (propName == ST_CRITERIA_SECURITY_ROLE) {
                                if (!Record.readOnlyCriteriaReverseCache[PSTFieldNames.PSTCreatedBy]) Record.readOnlyCriteriaReverseCache[PSTFieldNames.PSTCreatedBy] = [];
                                Record.readOnlyCriteriaReverseCache[PSTFieldNames.PSTCreatedBy].push(f.ID);
                            } else {
                                if (!Record.readOnlyCriteriaReverseCache[propName]) Record.readOnlyCriteriaReverseCache[propName] = [];
                                Record.readOnlyCriteriaReverseCache[propName].push(f.ID);
                            }

                            if (chunks[3] && ListDataMetaData[chunks[3].trim()]) {
                                if (!Record.readOnlyCriteriaReverseCache[chunks[3].trim()]) Record.readOnlyCriteriaReverseCache[chunks[3].trim()] = [];
                                Record.readOnlyCriteriaReverseCache[chunks[3].trim()].push(f.ID);
                            }
                        }
                    }
                    readOnlyCriteriaObj.criteria = f.ReadOnlyCriteria;
                    Record.readOnlyCriteriaCache[f.ID] = readOnlyCriteriaObj;
                }
            }
        }
    }

    public constructAdornerVisibilityCriteriaCaches(adornersMap: { [id: string]: Adorner }) {
        for (let key in adornersMap) {
            let adorner = adornersMap[key];
            if (adorner.VisibilityCriteria && adorner.Location) {
                let m = this.getCriteriaMatches(adorner.VisibilityCriteria);
                adorner.VisibilityCriteria = m.criteria;
                let visibilityCriteriaObj = new CriteriaObj();
                if (m.matches) {
                    for (let match of m.matches) {
                        let chunks = match.trim().split(this.propertyWithOperatorAndValuePattern);
                        if (chunks.length > 1) {
                            let prop = "";
                            let propName = chunks[1].trim();
                            if (
                                (adorner.Location == this.adornersIDS.TopicCardTypeColor && propName == PSTFieldNames.PSTTopicType)
                                || ((adorner.Location == this.adornersIDS.AssignmentIcon) && propName == PSTFieldNames.PSTAssignmentCoverageType)
                            ) {
                                for (let objType in Record.allPropertiesCache) {
                                    if (Record.allPropertiesCache[objType][propName] || ListDataMetaData[propName]) {
                                        visibilityCriteriaObj.hasPropForObjectType[objType] = true;
                                    }
                                }
                                if (chunks[2].trim() == '=') chunks[2] = '==';
                                prop += "this.itemType.toUpperCase()" + chunks[2] + chunks[3] + ".toUpperCase()";
                            } else {
                                prop = this.parseCriteriaChunks(chunks, visibilityCriteriaObj, m.buffer[match]);
                            }
                            if (prop !== null) {
                                adorner.VisibilityCriteria = adorner.VisibilityCriteria.replace(match.trim(), prop);
                            }
                        }
                    }

                    visibilityCriteriaObj.criteria = adorner.VisibilityCriteria;

                    if (!Record.adornerVisibilityCriteriaCache[adorner.Location]) Record.adornerVisibilityCriteriaCache[adorner.Location] = [];
                    Record.adornerVisibilityCriteriaCache[adorner.Location].push({ visibilityCriteria: visibilityCriteriaObj, adorner: adorner });
                }
            }
        }

        for (let l in Record.adornerVisibilityCriteriaCache) {
            let vcsArr = Record.adornerVisibilityCriteriaCache[l];
            vcsArr.sort((a, b) => {
                return a.adorner.Order - b.adorner.Order;
            });
        }
    }

    public objectDefaultValueFieldCache: { [id: number]: { [id: string]: any } } = {};
    public objectCopyFromParentFieldCache: { [id: number]: { [id: string]: any } } = {};
    private defaultValueFieldCache: { [id: string]: any } = {};

    public populateExtraFiltersCallback: any = null;

    public populateCopyFromParentCacheCallbacks: { [id: number]: (f: FieldDef) => void } = {};
    public populateCopyFromParentCache() {
        for (let f of this.FieldDefs) {
            for (let key in this.populateCopyFromParentCacheCallbacks) {
                this.populateCopyFromParentCacheCallbacks[key](f);
            }
        }
    }

    public populateDefaultValueCallbacks: { [id: number]: (f: FieldDef, criteriaObj: any) => void } = {};
    public populateDefaultValue() {
        for (let f of this.FieldDefs) {
            if (f.DefaultValue && f.DefaultValue.trim() != "") {
                let criteriaObj = {
                    key: f.ID,
                    value: "",
                    validation: f.Validation.toUpperCase()
                }

                if (f.Validation.toUpperCase() == "COMBOBOX") {
                    //debugger;
                    let listValues = f.List.split('|')
                    if (listValues.length > 0) {
                        if (listValues.indexOf(f.DefaultValue) > -1) {
                            criteriaObj.value = f.DefaultValue;
                        } else {
                            criteriaObj.value = "";
                        }
                    } else {
                        criteriaObj.value = "";
                    }
                } else if (f.Validation.toUpperCase() == "CHECKBOX") {
                    if (f.DefaultValue.toUpperCase() == "TRUE" || f.DefaultValue == "1") {
                        criteriaObj.value = "1";
                    } else if (f.DefaultValue.toUpperCase() == "FALSE" || f.DefaultValue == "0") {
                        criteriaObj.value = "0";
                    } else {
                        criteriaObj.value = "";
                    }
                } else {
                    criteriaObj.value = f.DefaultValue;
                }

                for (let key in this.populateDefaultValueCallbacks) {
                    this.populateDefaultValueCallbacks[key](f, criteriaObj);
                }
                this.defaultValueFieldCache[f.ID] = criteriaObj;
            }
        }
    }

    public getDefaultValue(fieldID: string): any {
        return this.defaultValueFieldCache[fieldID];
    }

    private securityRolesCache: { [id: string]: any } = {};

    public populateSecurityRoles(roles: any) {
        for (let role of roles) {
            this.securityRolesCache[role["ID"]] = role;
        }
    }

    public getSecurityRole(roleId: string): any {
        return (this.securityRolesCache[roleId]) ? this.securityRolesCache[roleId] : null;
    }

    private listColumnsForSpecialFieldCache: { [id: string]: Array<{ [id: string]: any }> } = {};
    public getListColumnsForSpecialField(field: string): Array<{ [id: string]: any }> {
        if (this.listColumnsForSpecialFieldCache[field]) return this.listColumnsForSpecialFieldCache[field];
        this.listColumnsForSpecialFieldCache[field] = [];
        for (let f of this.FieldDefs) {
            if (f.ID == field && ((f.Validation.toUpperCase() == "SPECIAL") || (f.Validation.toUpperCase() == "COST"))) {
                let columns = f.List ? f.List.split("|") : [];
                if (f.Validation.toUpperCase() == "COST") {
                    columns = ["CostType,C\\", "Cost,Text,15"];
                }
                for (let col of columns) {
                    let chunks = col.split(",")
                    let colName = chunks[0];
                    let validationType = chunks[1].toUpperCase();
                    let colWidth = 100;
                    let list: { id: string, title: string }[] = [];
                    if (chunks.length > 1) {
                        if (chunks[1].indexOf("C\\") > -1) {
                            validationType = "COMBOBOX";
                        }
                        if (f.Validation.toUpperCase() == "SPECIAL") {
                            let listValues = chunks[1].split("\\");
                            list = listValues.splice(1).map((v) => { return { id: v, title: this.calculateListItemTitle(field, v, v) } });
                        } else if (f.Validation.toUpperCase() == "COST") {
                            list = f.List ? f.List.split("|").map((v) => { return { id: v, title: this.calculateListItemTitle(field, v, v) } }) : []
                        }
                    } else {
                        validationType = "TEXT";
                    }
                    if (chunks.length > 2) {
                        if (chunks[2].trim()) colWidth = parseInt(chunks[2].trim());
                    }

                    let columnMetadata = {
                        header: colName,
                        validation: validationType,
                        columnWidth: colWidth,
                        list: list
                    };
                    this.listColumnsForSpecialFieldCache[field].push(columnMetadata);
                }
                break;
            }
        }
        return this.listColumnsForSpecialFieldCache[field];
    }

    private listColumnsForAddressFieldCache: Array<{ [id: string]: any }> = [];
    public getListColumnsForAddress(): Array<{ [id: string]: any }> {
        if (this.listColumnsForAddressFieldCache && this.listColumnsForAddressFieldCache.length > 0) return this.listColumnsForAddressFieldCache;
        this.listColumnsForAddressFieldCache = [];
        let columns = "Country,Text|AdministrativeArea,Text|Locality,Text|StreetAddress,Text|LocationName,Text|PostalCode,Text|Latitude,Text|Longitude,Text|LocationId,Text|FormattedAddress,Text|ShortCountry,Text|ShortAdministrativeArea,Text|Title,Text|SubLocality,Text|Neighborhood,Text|City,Text|State,Text".split("|");
        for (let col of columns) {
            let chunks = col.split(",")
            let colName = chunks[0];
            let validationType = chunks[1].toUpperCase();
            let colWidth = 100;
            let list = [];
            if (chunks.length > 1) {
                if (chunks[1].indexOf("C\\") > -1) {
                    validationType = "COMBOBOX";
                }
                let listValues = chunks[1].split("\\");
                list = listValues.splice(1);
            } else {
                validationType = "TEXT";
            }
            if (chunks.length > 2) {
                if (chunks[2].trim()) colWidth = parseInt(chunks[2].trim());
            }

            let columnMetadata = {
                header: colName,
                validation: validationType,
                columnWidth: colWidth,
                list: list
            };
            this.listColumnsForAddressFieldCache.push(columnMetadata);
        }
        return this.listColumnsForAddressFieldCache;
    }

    public setFieldDefCallbacks: { [id: number]: (fieldDef: FieldDef) => void } = {};
    public setFieldDefs(fieldDefsRes: any) {
        this.FieldDefs = [];
        this.FieldDefsDict = {};
        for (let i = 0; i < fieldDefsRes.length; i++) {
            let fieldDef: FieldDef = Object.assign(new FieldDef, fieldDefsRes[i]);
            if (!fieldDef.FieldGroup || fieldDef.FieldGroup == 'undefined') fieldDef.FieldGroup = "";
            this.FieldDefs.push(fieldDef);
            this.FieldDefsDict[fieldDef.ID] = fieldDef;

            if (['TIME', 'DATE', 'MOSDATETIME'].indexOf(fieldDef.Validation.toUpperCase()) > -1) {
                this.isDateTimeFieldTypeCache[fieldDef.ID] = true;
            }

            for (let key in this.setFieldDefCallbacks) {
                this.setFieldDefCallbacks[key](fieldDef);
            }
        }

        for (let key in this.calculateDefaultGroupSort) {
            this.calculateDefaultGroupSort[key]();
        }

        this.fieldDefsLoadedSource.next(true);
    }

    private setWireCategories(wireCategories: any) {
        let wireCategoriesDict: { [id: string]: TreeChecklistNode } = {};

        let rootGroup: TreeChecklistNode = new TreeChecklistNode();
        rootGroup.id = "@root";
        rootGroup.title = "ROOT";
        rootGroup.children = [];
        wireCategoriesDict["ROOT"] = rootGroup;

        for (let i = 0; i < wireCategories.length; i++) {
            let name = wireCategories[i]["CategoryName"];
            let group = wireCategories[i]["CategoryGroup"];

            let wireCategory: TreeChecklistNode = new TreeChecklistNode();
            wireCategory.id = name;
            wireCategory.title = name;
            wireCategory.children = [];

            if (group != "ROOT" && !wireCategoriesDict[group]) {
                let wireGroup: TreeChecklistNode = new TreeChecklistNode();
                wireGroup.id = group;
                wireGroup.title = group;
                wireGroup.children = [];
                wireCategoriesDict[group] = wireGroup;
                wireCategoriesDict["ROOT"].children.push(wireGroup);
            }

            wireCategoriesDict[group].children.push(wireCategory);
        }

        this.WireCategories = wireCategoriesDict["ROOT"].children;
    }

    private setWireProviders(wireProviders: any) {
        this.idToDescriptionMap[OPTION_BLANK] = this.languageSettings.PSTWebBlankOption;
        for (let i = 0; i < wireProviders.length; i++) {
            let id = wireProviders[i]["ProviderID"].replace("P_", "");
            let title = wireProviders[i]["ProviderName"];
            this.WireProviders.push({ "id": id, "title": title });
            this.idToDescriptionMap[id] = title;
        }
    }

    private setWirePriority() {
        this.idToDescriptionMap["WIRE_PRIORITY_0"] = "Any";
        this.idToDescriptionMap["WIRE_PRIORITY_1"] = "Flash";
        this.idToDescriptionMap["WIRE_PRIORITY_2"] = "Bulletin";
        this.idToDescriptionMap["WIRE_PRIORITY_3"] = "Urgent";
        this.idToDescriptionMap["WIRE_PRIORITY_4"] = "Regular";
        this.idToDescriptionMap["WIRE_PRIORITY_5"] = "Daily";
        this.idToDescriptionMap["WIRE_PRIORITY_6"] = "At Will";
    }

    public isValidationTypeImplemented(type: string) {
        return (this.ImplementedValidationTypes.indexOf(type.toUpperCase()) > -1)
    }

    public Staff: { [id: string]: Staff } = {};

    public securityPrivileges: { [id: string]: GroupSecurityPrivs } = {};

    private listItemsForDeskCache: {[id: number]: Array<{ id: string, title: string, selectable: boolean, initials?: string }> } = {};
    public idToDescriptionMap: { [id: string]: string } = {};
    public getListItemsForDesk(type: number): Array<{ id: string, title: string, selectable: boolean, initials?: string }> {
        if (this.listItemsForDeskCache[type]?.length > 0) return this.listItemsForDeskCache[type];
        if (!this.listItemsForDeskCache[type]) this.listItemsForDeskCache[type] = [];
        if (this.FieldDefs.length > 0) {
            let f = this.FieldDefsDict[PSTFieldNames.PSTTopicDepartment]
            let chunks = f.List ? f.List.split("|") : [];
            for (let c of chunks) {
                this.idToDescriptionMap[c] = c;
                const title = this.calculateListItemTitle(f.ID, c, c)
                if (!this.securityPrivileges[c]) {
                    if (this.securityPrivileges[this.nomWebServicesJson.PSTGroup]) {
                        this.listItemsForDeskCache[type].push({
                            id: c,
                            title,
                            selectable: this.calculateEditableForDesk(this.nomWebServicesJson.PSTGroup, type),
                            initials: this.calculateInitials(title)
                        });
                    }
                } else {
                    if (this.securityPrivileges[c]) {
                        this.listItemsForDeskCache[type].push({
                            id: c,
                            title,
                            selectable: this.calculateEditableForDesk(c, type),
                            initials: this.calculateInitials(title)
                        });
                    }
                }
            }
            for (let key in this.Groups) {
                let group = this.Groups[key];
                if (group.PlaybookGroup) {
                    this.idToDescriptionMap[group.ID] = group.Description;

                    const title = this.calculateListItemTitle(group.ID, group.Description, group.Description);
                    if (this.securityPrivileges[group.ID]) {
                        if (this.securityPrivileges[group.ID]) {
                            this.listItemsForDeskCache[type].push({
                                id: group.ID,
                                title,
                                selectable: this.calculateEditableForDesk(group.ID, type),
                                initials: this.calculateInitials(title)
                            });
                        }
                    } else {
                        if (this.securityPrivileges[this.nomWebServicesJson.PSTGroup]) {
                            this.listItemsForDeskCache[type].push({
                                id: group.ID,
                                title,
                                selectable: this.calculateEditableForDesk(this.nomWebServicesJson.PSTGroup, type),
                                initials: this.calculateInitials(title)
                            });
                        }
                    }
                }
            }
            return this.listItemsForDeskCache[type];
        } else {
            return [];
        }
    }

    public getListItemsForWireProvider(): Array<any> {
        return this.WireProviders;
    }

    public getListItemsForWireCategory(): Array<any> {
        return this.WireCategories;
    }

    public getListItemsForTeamLocations(): Array<any> {

        return this.PlaybookTeamsArray.filter(t => t.PlaybookTeam).map(team => {
            return {
                id: team.ID,
                title: team.Description,
                selectable: true
            }
        });
    }

    public clearDeskListItemCache() {
        this.listItemsForDeskCache = [];
        this.idToDescriptionMap = {};
    }

    public async getGlobalTablesSimple(): Promise<ServiceResponse<void>> {
        let response: HttpResponse<any> = await this.nomWebServicesJson.get(
            "/api/GlobalTables",
            {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                params: {
                    filterLangPrefix: 'PSTWeb',
                    filterInActiveUsers: 'True'
                },
                observe: 'response'
            });
        let res = response.body;
        this.languageSettings.populate(res._LanguageSettings);
        this.clearTitleCache();

        this.Apps = res._Apps;

        this.visualStateService.ConfigSettings = {};
        for (let configSetting of res._ConfigSettings) {
            this.visualStateService.ConfigSettings[configSetting.FieldName] = configSetting.FieldValue;
        }

        if (!this.visualStateService.ConfigSettings["MaxRows"] || this.visualStateService.ConfigSettings["MaxRows"] > 10000) {
            this.visualStateService.ConfigSettings["MaxRows"] = 10000;
        }
        this.setIsGroupSingleValue();
        this.setPlaybookHelpUrl();
        this.setEnforceRequiredFieldsOnCreate();

        this.Groups = {};
        this.PlaybookTeamsArray = [];
        this.GroupsArray = [];
        for (let group of res._Groups) {
            if (group.PlaybookGroup || group.PlaybookTeam || group.PSTGroup) {
                this.Groups[group.ID] = new Group(
                    group.ID,
                    group.Description,
                    group.Server,
                    group.WarnApproved,
                    group.MediaFTP,
                    group.PSTGroup,
                    group.PlaybookGroup,
                    group.PlaybookTeam);
                this.GroupsArray.push(this.Groups[group.ID]);

                if (group.PSTGroup == true) {
                    this.nomWebServicesJson.PSTGroup = group.ID;
                    this.nomWebServicesJson.PSTServer = group.Server;
                    this.PlaybookTeamsArray.push(this.Groups[group.ID]);
                    this.DefaultPlaybookTeam = this.Groups[group.ID];
                    this.EnterprisePlaybookGroup = group.ID;
                    this.nomWebServicesJson.currentPlaybookTeam = this.DefaultPlaybookTeam.ID;
                } else if (group.PlaybookTeam == true) {
                    this.PlaybookTeamsArray.push(this.Groups[group.ID]);
                }
            }
        }
        if (!this.nomWebServicesJson.PSTGroup || !this.nomWebServicesJson.PSTServer) {
            this.nomWebServicesJson.PSTServer = this.nomWebServicesJson.HelloResults.HomeDatabase;
        }

        this.Languages = [];
        for (let language of res._AvailableLanguages) {
            var languageId = language.BaseLanguage + language.Dialect;
            var languageInfo = new ENPSLanguage(languageId, language.FriendlyName)
            if (languageId == this.nomWebServicesJson.HelloResults.PersonalLanguage) {
                this.PersonalLanguage = languageInfo;
            }
            this.Languages.push(languageInfo);
        }
        if (this.nomWebServicesJson.HelloResults.PersonalLanguage) {
            moment.locale(this.nomWebServicesJson.HelloResults.PersonalLanguage.toLowerCase());
        }

        this.MOSDevices = res._MOSDevices;
        this.Privileges = res._Privileges;

        this.externalActionsService.ExternalActions = [];
        for (let action of res._ExternalActions) {
            this.externalActionsService.ExternalActions.push(new ExternalAction(action.ID, action.Description, action.ActionEndpoint, action.ActionType, action.TopicAction, action.AssignmentAction, action.PublicationListAction, action.Icon, action.Verb, action.Headers, action.VisibilityCriteria));
            Record.allActionsCache[action.ID] = new ExternalAction(action.ID, action.Description, action.ActionEndpoint, action.ActionType, action.TopicAction, action.AssignmentAction, action.PublicationListAction, action.Icon, action.Verb, action.Headers, action.VisibilityCriteria)
        }

        this.FieldMappings = [];
        if (res._FieldMapping) {
            for (let mapping of res._FieldMapping) {
                this.FieldMappings.push(new FieldMapping(
                    mapping.SourceObjectType,
                    mapping.DestinationObjectType,
                    mapping.SourceFieldId,
                    mapping.DestinationFieldId,
                    mapping.CopyToTraget,
                    mapping.CopyToChild,
                    mapping.UpdateToChild,
                    mapping.UpdateToParent,
                    mapping.DateRule,
                    mapping.UpdateToLinkedObject,
                    mapping.PublicationChannelType
                ));
            }
        }

        this.Servers = {};
        for (let server of res._Servers) {
            this.Servers[server.ServerID] = new Server(server.ServerID, server.Description, server.ServerGroup);
        }

        this.Staff = {};
        for (let staff of res._Staff) {
            this.Staff[staff.UserID] = new Staff(
                staff.UserID,
                staff.GivenName,
                staff.GroupDescription,
                staff.Program,
                staff.ServerDescription,
                staff.ServerID,
                staff.Supervisor,
                staff.Surname,
                staff.Email,
                staff.Title,
                staff.Phone,
                staff.GivenName + " " + staff.Surname,
                this.calculateInitials(`${staff.GivenName} ${staff.Surname}`),
            );
        }

        this.StorytellingNavigation = [];
        this.StorytellingNavigationDict = {};
        for (let storytellingNavigation of res._StorytellingNavigation) {
            this.StorytellingNavigation.push(storytellingNavigation)
            this.StorytellingNavigationDict[storytellingNavigation.Product] = storytellingNavigation;
        }
        this.StorytellingNavigation.sort((a, b) => (a.Sequence > b.Sequence) ? 1 : ((b.Sequence > a.Sequence) ? -1 : 0));

        this.CustomFilterDef = [];
        // ?dd All and Blank by default
        let allCustomFilterDef: CustomFilterDef = new CustomFilterDef("ALL", this.languageSettings.PSTWebAllOption, "Date", "all");
        let blankCustomFilterDef: CustomFilterDef = new CustomFilterDef("BLANK", this.languageSettings.PSTWebBlankOption, "Date", "blank");
        this.CustomFilterDef.push(allCustomFilterDef);
        this.CustomFilterDefsDict["ALL"] = allCustomFilterDef;
        this.CustomFilterDef.push(blankCustomFilterDef);
        this.CustomFilterDefsDict["BLANK"] = blankCustomFilterDef;

        for (let filter of res._CustomFilters) {
            let customFilter: CustomFilterDef = new CustomFilterDef(filter.ID, filter.DefaultCaption, filter.Validation, filter.FilterValue);
            this.CustomFilterDef.push(customFilter);
            this.CustomFilterDefsDict[filter.ID] = customFilter;
        }

        this.CommonTimeZones = [];
        this.IanaTimeZones = [];


        const getTimezoneLabel = (tzId: string, title: string) => {
            const timeZoneLabel = 'PSTWebTimeZone';
            if (this.languageSettings.get(`${timeZoneLabel}_${tzId}`).includes(timeZoneLabel)) {
                return title;
            }
            return this.languageSettings.get(`${timeZoneLabel}_${tzId}`);
        }
        if (res._CommonTimeZones) {
            res._CommonTimeZones.forEach(tz => {
                this.CommonTimeZones.push(new TimeZone(tz.TZID, getTimezoneLabel(tz.TZID, tz.DisplayName)))
            });
            moment.tz.names().forEach(currentValue => {
                let isExisting: boolean = false;
                res._CommonTimeZones.forEach(tz => {
                    if (tz.TZID == currentValue) {
                        isExisting = true;
                        return;
                    }
                })
                if (!isExisting) {
                    // Now show TimeZones even with offset that is not a whole number of hours
                    this.IanaTimeZones.push(new TimeZone(currentValue, getTimezoneLabel(currentValue, currentValue)));
                }
            });
            let allTimeZones: Array<TimeZone> = [...this.CommonTimeZones, ...this.IanaTimeZones];
            this.DefaultTimeZone = new TimeZone("", "");
            let clientTimeZoneId = '';
            try {
                clientTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch (err) {
                clientTimeZoneId = "America/New_York"
            }

            let clientTimeZone = allTimeZones.filter(t => {
                return t.id == clientTimeZoneId;
            });
            if (clientTimeZone && clientTimeZone.length > 0) {
                this.ClientTimeZone = new TimeZone(clientTimeZone[0].id, getTimezoneLabel(clientTimeZone[0].id, clientTimeZone[0].title));
            } else {
                let defaultTimeZone = allTimeZones.filter(t => {
                    return t.id == "America/New_York";
                });
                if (defaultTimeZone && defaultTimeZone.length > 0) {
                    this.ClientTimeZone = new TimeZone(defaultTimeZone[0].id, getTimezoneLabel(defaultTimeZone[0].id, defaultTimeZone[0].title));
                } else {
                    this.ClientTimeZone = new TimeZone("America/New_York", getTimezoneLabel("America/New_York", "America/New_York"));
                }
            }

            if (this.nomWebServicesJson.HelloResults && this.nomWebServicesJson.HelloResults.PersonalTimeZone) {
                let personalTimeZone = allTimeZones.filter(t => {
                    return t.id == this.nomWebServicesJson.HelloResults.PersonalTimeZone;
                });
                if (personalTimeZone && personalTimeZone.length > 0) {
                    this.PersonalTimeZone = new TimeZone(personalTimeZone[0].id, getTimezoneLabel(personalTimeZone[0].id, personalTimeZone[0].title));
                    this.PersonalTimeZoneDisplay = this.PersonalTimeZone;
                } else {
                    this.PersonalTimeZone = new TimeZone(this.ClientTimeZone.id, getTimezoneLabel(this.ClientTimeZone.id, this.ClientTimeZone.title));
                    this.PersonalTimeZoneDisplay = this.PersonalTimeZone;
                }
            } else {

                this.PersonalTimeZoneDisplay = null;
            }

            if (this.nomWebServicesJson.HelloResults && this.nomWebServicesJson.HelloResults.PersonalLanguage) {
                let personalLanguage = this.Languages.filter(t => {
                    return t.id == this.nomWebServicesJson.HelloResults.PersonalLanguage;
                });
                if (personalLanguage && personalLanguage.length > 0) {
                    this.PersonalLanguage = new ENPSLanguage(personalLanguage[0].id, personalLanguage[0].title);
                } else {
                    this.PersonalLanguage = null;
                }
            } else {
                this.PersonalLanguage = null;
            }
        }

        this.Servers = {};
        for (let server of res._Servers) {
            this.Servers[server.ServerID] = new Server(server.ServerID, server.Description, server.ServerGroup);
        }

        if (this.nomWebServicesJson.HelloResults.SecurityPrivileges) {
            for (let priv of this.nomWebServicesJson.HelloResults.SecurityPrivileges) {
                this.securityPrivileges[priv.GroupId] = this.calculatePrivs(priv)
            }

            if (!this.securityPrivileges[this.nomWebServicesJson.PSTGroup]) {
                this.securityPrivileges[this.nomWebServicesJson.PSTGroup] = new GroupSecurityPrivs()
            }

        }

        this.Adorners = {};
        for (let adorner of res._Adorners) {
            this.Adorners[adorner.ID] = new Adorner(adorner.ID, adorner.AdornerLocation, adorner.Color, adorner.Icon, adorner.IconUrl, adorner.VisibilityCriteria, adorner.Order);
        }

        let ret = await this.getGlobalFields();
        let wireCategoriesAndProviders = await this.getWireCategoriesAndProviders();

        this.constructAdornerVisibilityCriteriaCaches(this.Adorners);
        this.nomWebServicesJson.sendServiceData();
        return ret;
    }

    public async getGlobalFields(): Promise<ServiceResponse<void>> {

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get(
                "/api/GlobalFields",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        filterLangPrefix: 'PSTWeb',
                        filterInActiveUsers: 'True'
                    },
                    observe: 'response'
                });
        } catch (error) {
            throw error;
        }
        let res = response.body;
        this.setFieldDefs(res);
        this.constructCriteriaMappings();
        this.populateDefaultValue();
        this.populateCopyFromParentCache();
        return new ServiceResponse<void>(true);
    }

    public async getWireCategoriesAndProviders(): Promise<ServiceResponse<void>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get(
                "/api/WireCategoriesAndProviders",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            throw error;
        }
        let res = response.body;
        this.setWireCategories(res["WireCategories"]);
        this.setWireProviders(res["WireProviders"]);
        this.setWirePriority();
        return new ServiceResponse<void>(true);
    }

    protected calculatePrivs(priv: any): GroupSecurityPrivs {
        let res = new GroupSecurityPrivs()
        if (!this.nomWebServicesJson.HelloResults.Supervisor) {
            if (priv.TopicPrivileges != GroupPermissions.READWRITE) {
                res.topic.write = false;
            }
            if (priv.TopicPrivileges == GroupPermissions.NONE) {
                res.topic.read = false;
            }

            if (priv.AssignmentPrivileges != GroupPermissions.READWRITE) {
                res.assignment.write = false;
            }
            if (priv.AssignmentPrivileges == GroupPermissions.NONE) {
                res.assignment.read = false;
            }

            if (priv.CostPrivileges != GroupCostPermissions.READWRITE) {
                res.cost.write = false;
            }
            if (priv.CostPrivileges == GroupCostPermissions.NONE) {
                res.cost.read = false;
            }
            if (!priv.ViewUserLocationPrivileges) {
                res.viewUserLocationPrivileges = false;
            }
        }
        res.privId = priv.PrivId;
        return res;
    }

    public getRtl(): Direction {
        return this.nomWebServicesJson?.HelloResults?.Rtl ? 'rtl' : 'ltr';
    }

}
